import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  Dropdown,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import { FiBookmark, FiChevronDown } from "react-icons/fi";
import SearchArticle from "../components/SearchArticle";
import BreadcrumbBar from "../components/Breadcrumb";
import { Link } from "react-router-dom";
import ex1 from "../assets/images/ex1.png";

const MOCK_RESULTS = [
  {
    id: 1,
    title:
      "Learning the natural history of human disease with generative transformers",
    authors: [
      "Wang Q",
      "Xiao ZX",
      "Zheng X",
      "Xiang N",
      "Lasse Holst Mortensen",
      "Alexander Wolfgang Jung",
      "Schott G",
      "Ewen Birney",
      "Kumar Gururur",
    ],
    journal: "Nature Medicine",
    date: "2025 Sep 17",
    doi: "10.1038/s41586-025-05923-3",
    pmid: "26978177",
    labels: ["Clinical Trial"],
    image: ex1,
    abstract: `
  Decision-making in healthcare relies on understanding patients’ past and current health states to predict and, ultimately, change their future course. Artificial intelligence (AI) methods promise to aid this task by learning patterns of disease progression from large corpora of health records. However, their potential has not been fully investigated at scale. Here we modify the GPT® (generative pretrained transformer) architecture to model the progression and compounding nature of human diseases. We train this model, Delphi-2M, on data from 4.0 million UK Biobank participants and validate it using external data from 1.7 million Danish individuals with no change in parameters. Delphi-2M predicts the rates of more than 1,000 diseases, conditional on each individual’s past disease history, with accuracy comparable to that of existing single-disease models. Delphi-2M’s generative nature also enables sampling of synthetic future health trajectories, providing meaningful estimates of potential disease burden for up to 20 years, and enabling the training of AI models that have never seen actual data. Explainable AI methods provide insights into Delphi-2M’s predictions, revealing clusters of co-morbidities within and across disease chapters and their time-dependent consequences on future health, but also highlight biases learnt from training data. In summary, transformer-based models appear to be well suited for predictive and generative health-related tasks, are scalable to population-scale datasets and provide insights into temporal dependencies between disease events, potentially improving the understanding of personalized health risks and informing precision medicine approaches.
  `,
  },
  {
    id: 2,
    title:
      "Precision Psychiatry Applications with Pharmacogenomics: Artificial Intelligence and Machine Learning Approaches",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 3,
    title:
      "Association among basal serum BDNF, cardiorespiratory fitness and cardiovascular disease risk factors in untrained healthy Korean men",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 4,
    title:
      "In Vivo CD19 CAR T-Cell Therapy for Refractory Systemic Lupus Erythematosus",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 5,
    title:
      "Fully Human Anti-CD19 CAR T Cells Derived from Systemic Lupus Erythematosus Patients Exhibit Cytotoxicity with Reduced Inflammatory Cytokine Production",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
];

export default function MedicalArticleSearchPage() {
  const [sortBy, setSortBy] = useState("Recent Article");
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const totalPages = 10;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MOCK_RESULTS;
    return MOCK_RESULTS.filter((r) =>
      [r.title, r.journal, r.doi, r.pmid, ...(r.authors || [])]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [query]);

  const toggleSave = (id) => {
    setSaved((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <>
      <SearchArticle />
      <BreadcrumbBar
        items={[{ label: "Home", href: "/" }, { label: "Articles" }]}
      />
      <Container className="list-section">
        <Row className="align-items-center mb-3 g-2">
          <Col
            xs="4"
            md="6"
            className="d-flex align-items-center justify-content-start"
          >
            <Button
              variant="outline-secondary"
              className="mobile-filter-toggle d-md-none"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </Col>
          <Col
            xs="8"
            md="6"
            className="d-flex align-items-center justify-content-end"
          >
            <div className="sort-label">SORT BY</div>
            <Dropdown onSelect={(k) => setSortBy(k || "Recent Article")}>
              <Dropdown.Toggle
                className="sort-toggle"
                size="sm"
                variant="light"
              >
                {sortBy}
                <FiChevronDown className="ms-2" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="Recent Article">
                  Recent Article
                </Dropdown.Item>
                <Dropdown.Item eventKey="Most Cited">Most Cited</Dropdown.Item>
                <Dropdown.Item eventKey="Relevance">Relevance</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        <Row>
          {/* Sidebar for filters */}
          <Col xl={3} lg={4} md={5}>
            <Card
              className={`filters-card ${
                showFilters ? "d-block" : "d-none d-md-block"
              }`}
            >
              <Card.Body className="p-0">
                <SidebarSection title="Publication Date">
                  <Form.Check
                    name="pubdate"
                    type="radio"
                    id="y1"
                    label="1 Year"
                  />
                  <Form.Check
                    name="pubdate"
                    type="radio"
                    id="y5"
                    label="5 Years"
                  />
                  <Form.Check
                    name="pubdate"
                    type="radio"
                    id="y10"
                    label="10 Years"
                  />
                  <Form.Check
                    name="pubdate"
                    type="radio"
                    id="cust"
                    label="Custom range"
                  />
                </SidebarSection>
                <SidebarSection
                  className="checkbox-elements scrollable"
                  title="Disease Type"
                  scroll
                >
                  {[
                    "Cardiovascular Diseases",
                    "Respiratory Diseases",
                    "Neurological Disorders",
                    "Endocrine Disorders",
                    "Musculoskeletal Disorders",
                    "Gastrointestinal Diseases",
                    "Genetic and Rare Diseases",
                  ].map((lbl, i) => (
                    <Form.Check
                      key={i}
                      type="checkbox"
                      id={`dt-${i}`}
                      label={lbl}
                    />
                  ))}
                </SidebarSection>
                <SidebarSection title="Article Type">
                  {[
                    "Books and Documents",
                    "Clinical Trial",
                    "Meta‑Analysis",
                    "Review",
                    "Systematic Review",
                  ].map((lbl, i) => (
                    <Form.Check
                      key={i}
                      type="checkbox"
                      id={`at-${i}`}
                      label={lbl}
                    />
                  ))}
                </SidebarSection>
              </Card.Body>
            </Card>
          </Col>

          {/* Results */}
          <Col xl={9} lg={8} md={7}>
            <div className="results-stack">
              {filtered.map((r) => (
                <Card key={r.id} className="result-card">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <Card.Title className="result-title">
                          {r.title}
                        </Card.Title>
                        <div className="author-chips">
                          {r.authors.map((a, idx) => (
                            <Badge key={idx} className="author-badge">
                              {a}
                            </Badge>
                          ))}
                        </div>
                        <div className="meta-line">
                          <a href="#">
                            {r.journal} {r.date}. doi: {r.doi}.
                          </a>
                        </div>
                        <div className="meta-sub">PMID: {r.pmid}</div>
                        <div className="label-row">
                          {r.labels.map((l, i) => (
                            <p key={i}>{l}</p>
                          ))}
                        </div>
                        <div className="mt-2">
                          <Button
                            as={Link}
                            to={`/explore/${r.id}`}
                            state={{ item: r }}
                            size="sm"
                            variant="light"
                            className="read-more-btn"
                          >
                            READ MORE
                          </Button>
                        </div>
                      </div>

                      <Button
                        variant="outline-secondary"
                        className={`save-btn ${
                          saved.has(r.id) ? "is-saved" : ""
                        }`}
                        onClick={() => toggleSave(r.id)}
                        aria-label="Save"
                      >
                        <FiBookmark />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-center mt-3">
              <Pagination className="mas-pagination">
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {[1, 2, 3, 4, 5].map((n) => (
                  <Pagination.Item
                    key={n}
                    active={n === currentPage}
                    onClick={() => handlePageChange(n)}
                  >
                    {n}
                  </Pagination.Item>
                ))}
                <Pagination.Ellipsis disabled />
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
}

function SidebarSection({ title, children, scroll = false, className = "" }) {
  const hasCheckbox = React.Children.toArray(children).some(
    (child) => child?.props?.type === "checkbox"
  );

  const finalClass = [
    "sidebar-section",
    scroll ? "scrollable" : "",
    hasCheckbox ? "checkbox-elements" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={finalClass}>
      <div className="section-title">{title}</div>
      <div className="section-body">{children}</div>
    </div>
  );
}
